/**
 * Hook for managing seeding state
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSeedingStatus,
  startSeeding,
  resetSeeding,
  type SeedingStatus,
  type SeedingResult,
} from '@/lib/photo-api';

export interface CountryInfo {
  code: string;
  name: string;
  capital: string;
}

// Rate limit constants
const RATE_LIMIT_PER_HOUR = 50;
const RATE_LIMIT_WAIT_MS = 60 * 60 * 1000; // 1 hour in ms
const REQUEST_THROTTLE_MS = 1000; // 1 request per second

export function useSeeding(countries: CountryInfo[]) {
  const [status, setStatus] = useState<SeedingStatus | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SeedingResult | null>(null);

  // Rate limit countdown state
  const [rateLimitedUntil, setRateLimitedUntil] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<{ minutes: number; seconds: number } | null>(null);

  const pollIntervalRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const resumeTimeoutRef = useRef<number | null>(null);
  const isSeedingRef = useRef(false);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    try {
      const data = await getSeedingStatus(countries.length);
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    }
  }, [countries.length]);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll status while seeding
  useEffect(() => {
    if (isSeeding && !rateLimitedUntil) {
      pollIntervalRef.current = window.setInterval(fetchStatus, 3000);
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isSeeding, rateLimitedUntil, fetchStatus]);

  // Countdown timer effect
  useEffect(() => {
    if (!rateLimitedUntil) {
      setCountdown(null);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = rateLimitedUntil.getTime() - now;

      if (remaining <= 0) {
        setCountdown(null);
        setRateLimitedUntil(null);
        return;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setCountdown({ minutes, seconds });
    };

    // Update immediately
    updateCountdown();

    // Then update every second
    countdownIntervalRef.current = window.setInterval(updateCountdown, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [rateLimitedUntil]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, []);

  // Start seeding process
  const start = useCallback(async () => {
    if (isSeedingRef.current && !rateLimitedUntil) return;

    // Clear any existing rate limit state when starting fresh
    if (rateLimitedUntil) {
      setRateLimitedUntil(null);
      setCountdown(null);
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    }

    isSeedingRef.current = true;
    setIsSeeding(true);
    setLoading(true);
    setError(null);

    try {
      const result = await startSeeding(countries);
      setLastResult(result);
      await fetchStatus();

      // Check if we've hit the rate limit
      if (result.remainingCountries > 0 && result.rateLimitRemaining < 2) {
        // Rate limit hit - set up countdown and auto-resume
        const resumeAt = new Date(Date.now() + RATE_LIMIT_WAIT_MS);
        setRateLimitedUntil(resumeAt);

        // Set up auto-resume timeout
        resumeTimeoutRef.current = window.setTimeout(() => {
          if (isSeedingRef.current) {
            setRateLimitedUntil(null);
            setCountdown(null);
            start();
          }
        }, RATE_LIMIT_WAIT_MS);

        setLoading(false);
        return;
      }

      // If there are still countries to process and we have rate limit,
      // continue seeding automatically with throttling (1 request per second)
      if (result.remainingCountries > 0 && result.rateLimitRemaining >= 2) {
        setTimeout(() => {
          if (isSeedingRef.current) {
            start();
          }
        }, REQUEST_THROTTLE_MS);
      } else if (result.remainingCountries === 0) {
        // All done!
        setIsSeeding(false);
        isSeedingRef.current = false;
      } else {
        setIsSeeding(false);
        isSeedingRef.current = false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seeding failed');
      setIsSeeding(false);
      isSeedingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [countries, fetchStatus, rateLimitedUntil]);

  // Pause seeding
  const pause = useCallback(() => {
    isSeedingRef.current = false;
    setIsSeeding(false);
    setRateLimitedUntil(null);
    setCountdown(null);
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  // Reset seeding progress
  const reset = useCallback(async (countryCodes?: string[]) => {
    setLoading(true);
    try {
      await resetSeeding(countryCodes);
      await fetchStatus();
      setLastResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  }, [fetchStatus]);

  // Calculate estimated time remaining
  const estimatedHoursRemaining = status
    ? Math.ceil((status.remainingCountries * 2) / 50)
    : 0;

  return {
    status,
    isSeeding,
    loading,
    error,
    lastResult,
    estimatedHoursRemaining,
    countdown,
    isRateLimited: !!rateLimitedUntil,
    start,
    pause,
    reset,
    refetch: fetchStatus,
  };
}
